import type { ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import type {
	IHelpsiteArticlesFields,
	IModuleFooterFields,
} from "@types-contentful";
import { richTextHelper, colours } from "@helpers";
import { helpsiteTrackLink } from "@helpers/helpsite-mixpanel";
import { BLOCKS } from "@contentful/rich-text-types";
import FooterModule from "@components/FooterModule";
import Icon from "@components/Icon";
import HelpBreadcrumb from "./HelpBreadcrumb";
import HelpArticleList from "./HelpArticleList";
import HelpMobileSupportFooter from "./HelpMobileSupportFooter";
import HelpDesktopSupportFooter from "./HelpDesktopSupportFooter";

const PageWrapper = styled.div`
	width: 100%;
	box-sizing: border-box;
	overflow-x: hidden;
	/* Remove extra margin at bottom of FooterModule on this page */
	margin-bottom: 0 !important;
`;

const CONTENT_PADDING_MOBILE = "15px";
const CONTENT_PADDING_DESKTOP = "24px";
const CONTENT_MAX_WIDTH = "1136px";
const BREAKPOINT_1024 = "1024px";

const ContentContainer = styled.div`
	width: 100%;
	box-sizing: border-box;
	padding: 0 ${CONTENT_PADDING_MOBILE} 40px;

	@media (min-width: ${BREAKPOINT_1024}) {
		max-width: ${CONTENT_MAX_WIDTH};
		margin-left: auto;
		margin-right: auto;
		padding: 0 ${CONTENT_PADDING_DESKTOP} 60px;
	}
`;

const TopicHeaderSection = styled.div`
	background-color: ${colours.WHITE};
	padding: 30px ${CONTENT_PADDING_MOBILE} 40px;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${BREAKPOINT_1024}) {
		max-width: ${CONTENT_MAX_WIDTH};
		margin-left: auto;
		margin-right: auto;
		padding: 40px ${CONTENT_PADDING_DESKTOP} 50px;
	}
`;

const TopicHeader = styled.h1`
	font-family: var(--taviraj-light);
	font-weight: 400;
	font-size: 30px;
	line-height: 1.4em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0 0 16px;

	@media (min-width: ${BREAKPOINT_1024}) {
		font-size: 44px;
		line-height: 1.4em;
	}
`;

const TopicContent = styled.div`
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.5;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;

	p {
		margin: 0 0 12px;
	}
	p:last-child {
		margin-bottom: 0;
	}
`;

const ArticleListSection = styled.section`
	margin-top: 32px;

	@media (min-width: ${BREAKPOINT_1024}) {
		margin-top: 48px;
	}
`;

const ViewAllHelpTopicsWrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	width: 100%;
	box-sizing: border-box;
	padding: 24px ${CONTENT_PADDING_MOBILE} 40px;

	@media (min-width: ${BREAKPOINT_1024}) {
		max-width: ${CONTENT_MAX_WIDTH};
		margin-left: auto;
		margin-right: auto;
		padding: 0px ${CONTENT_PADDING_DESKTOP} 40px;
	}
`;

const HelpSupportFooterWrapper = styled.div`
	margin-bottom: 60px;

	@media (min-width: ${BREAKPOINT_1024}) {
		margin-bottom: 80px;
	}
`;

const ViewAllHelpTopicsLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 12px 20px 12px;
	background-color: ${colours.WHITE};
	border: 1px solid rgba(0, 0, 0, 0.2);
	border-radius: 12px;
	text-decoration: none;
	color: ${colours.MIDNIGHT_BLUE};
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.27;
	box-sizing: border-box;

	&:hover {
		opacity: 0.9;
		color: ${colours.MIDNIGHT_BLUE};
		text-decoration: none;
	}
`;

interface HelpTopicProps {
	pageData: IHelpsiteArticlesFields;
	footer?: IModuleFooterFields | null;
}

const HelpTopic: React.FC<HelpTopicProps> = ({ pageData, footer }) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];
	const { title, content, articles, topicsRelativeUrl } = pageData;

	const richTextOptions = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (_node: unknown, children: ReactNode) => (
				<p>{children}</p>
			),
		},
	};

	const topicSlug = topicsRelativeUrl
		? topicsRelativeUrl.startsWith("/")
			? topicsRelativeUrl.slice(1)
			: topicsRelativeUrl
		: undefined;

	const breadcrumbItems = [{ label: "Help", href: "/help" }];

	return (
		<PageWrapper>
			<HelpBreadcrumb items={breadcrumbItems} />

			<TopicHeaderSection>
				{title && <TopicHeader>{title}</TopicHeader>}
				{content?.nodeType &&
					content.content &&
					Array.isArray(content.content) && (
						<TopicContent>
							{richTextHelper(content, richTextOptions)}
						</TopicContent>
					)}
			</TopicHeaderSection>

			{articles && articles.length > 0 && (
				<ContentContainer>
					<ArticleListSection>
						<HelpArticleList articles={articles} topicSlug={topicSlug} />
					</ArticleListSection>
				</ContentContainer>
			)}

			<ViewAllHelpTopicsWrapper>
				<ViewAllHelpTopicsLink
					href="/help"
					onClick={() =>
						helpsiteTrackLink({
							helpsite_section: "topic_view_all",
							page_path: pagePath,
							link_href: "/help",
							label: "View all help topics",
						})
					}
				>
					<Icon
						iconName="chevron-left"
						iconSizes={{
							iconMobile: 20,
							iconTablet: 20,
							iconLaptop: 20,
							iconDesktop: 20,
							iconDesktopLarge: 20,
						}}
						alt=""
					/>
					View all help topics
				</ViewAllHelpTopicsLink>
			</ViewAllHelpTopicsWrapper>

			<HelpSupportFooterWrapper>
				<HelpMobileSupportFooter />
				<HelpDesktopSupportFooter />
			</HelpSupportFooterWrapper>
			{footer && <FooterModule {...footer} />}
		</PageWrapper>
	);
};

export default HelpTopic;
